import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { cn } from '@hero/web-lib';
import { Checkbox } from '../../../ui/Checkbox';
import { PricingSheetRow } from './PricingSheetRow';
import {
  selectVisibleRows,
  usePricingSheetStore,
} from './store/usePricingSheetStore';

/**
 * Spreadsheet-style editable table. All visible rows are rendered together
 * (no virtualization yet — the sheet is meant to be used in batches of
 * tens-to-low-hundreds at a time; if we cross that we'll wire react-virtual).
 *
 * The table never renders an empty state itself; `PricingSheetPage` swaps
 * the entire table out for `PricingSheetEmpty` when `rows.length === 0`.
 * When a filter is active but matches no row, we keep the table chrome and
 * show a compact in-body hint so the user sees they're in "filtered" mode.
 */
export function PricingSheetTable() {
  const { t } = useTranslation(['m-pricing']);

  const side = usePricingSheetStore(s => s.side);
  // `selectVisibleRows` allocates a new filtered array on every snapshot
  // read; `useShallow` keeps the returned reference stable while element
  // identity is preserved (immer keeps row references when nothing changed).
  const visibleRows = usePricingSheetStore(useShallow(selectVisibleRows));
  const selectedIds = usePricingSheetStore(useShallow(s => s.selectedIds));
  const toggleAllVisible = usePricingSheetStore(s => s.toggleAllVisible);

  const visibleIds = useMemo(() => visibleRows.map(r => r.id), [visibleRows]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const allSelected =
    visibleRows.length > 0 && visibleRows.every(r => selectedSet.has(r.id));
  const someSelected =
    !allSelected && visibleRows.some(r => selectedSet.has(r.id));

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50/60">
            <th className="w-[40px] px-3 py-2">
              <Checkbox
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={v => toggleAllVisible(!!v, visibleIds)}
              />
            </th>
            <Th>{t('sheet.cols.origin', { defaultValue: 'Origin' })}</Th>
            {/*
              Decorative "→" gutter mirroring the filter strip's arrow so
              the eye reads origin-to-destination as a single phrase.
              Width matches the icon (size-3 = 12px) plus side padding.
            */}
            <th aria-hidden className="w-[24px] px-0" />
            <Th>{t('sheet.cols.destination', { defaultValue: 'Destination' })}</Th>
            <Th>{t('sheet.cols.vehicles', { defaultValue: 'Vehicles' })}</Th>
            <Th>{t('sheet.cols.trip', { defaultValue: 'Trip' })}</Th>
            <Th>{t('sheet.cols.load', { defaultValue: 'Load' })}</Th>
            <Th align="end">{t('sheet.cols.price', { defaultValue: 'Rate' })}</Th>
            <Th>{t('sheet.cols.status', { defaultValue: 'Status' })}</Th>
            <th aria-hidden className="w-[72px]" />
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(row => (
            <PricingSheetRow
              key={row.id}
              row={row}
              side={side}
              isSelected={selectedSet.has(row.id)}
            />
          ))}
          {visibleRows.length === 0 && (
            <tr>
              <td
                colSpan={10}
                className="px-6 py-8 text-center text-xs text-gray-400">
                {t('sheet.no_match', {
                  defaultValue:
                    'No rates match this filter. Clear it or add a new lane below.',
                })}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  align = 'start',
}: {
  children: React.ReactNode;
  align?: 'start' | 'end';
}) {
  return (
    <th
      className={cn(
        'px-1.5 py-2 text-[10px] font-medium uppercase tracking-wider text-gray-400',
        align === 'end' ? 'text-end' : 'text-start',
      )}>
      {children}
    </th>
  );
}
